import { SimpleTelemetryLogger, ISimpleTelemetryLogger } from './SimpleTelemetryLogger';
import { createActivity, IActivityScope } from './Activity';
import { Error } from './InternalContracts';
import { Activity } from './Contracts';
import { EventFlags, DataField, TelemetryEvent, CustomerContentEvent } from './DataModels';
import { ITelemetryConfig } from '.';
import { CV, getBaseCv } from './CorrelationVector';

export interface ErrorEvent {
  eventName: string;
  error: Error.Contract;
  eventFlags: EventFlags;
  dataFields?: DataField[];
}

export interface ITelemetryLogger extends ISimpleTelemetryLogger {
  /**
   * Creates a new activity. The caller is responsible for setting the result and calling endNow.
   * @param telemetryEvent - The underlying telemetry event for this activity
   */
  createActivity(telemetryEvent: TelemetryEvent, parent?: IActivityScope): IActivityScope;

  /**
   * Advanced usage: Sends an activity. The caller is responsible for setting all of the activity parameters.
   * @param activityName - The name of the activity
   * @param activity - The activity contract
   * @param dataFields - Optional additional data fields
   * @param eventFlags - Event flags
   */
  sendActivity(activityName: string, activity: Activity.Contract, dataFields?: DataField[], eventFlags?: EventFlags): void;

  /**
   * Sends an event with the Error data contract. A convenience wrapper around sendTelemetryEvent, with
   * an Error as a data field.
   * @param eventName - The name of the event
   * @param errorGroup - The group of the error
   * @param tag - The error tag
   * @param code - The error code
   * @param id - The error id
   * @param count - The count of the error
   * @param dataFields - Data fields to send with the event
   * @param eventFlags - Optional event flags to use for the error
   */
  sendError(telemetryEvent: ErrorEvent): void;

  /**
   * Creates a customer content activity, which should be ended by the caller by calling endNow().
   * @param customerContentEvent - The underlying customer content event for this activity
   */
  createCustomerContentActivity(customerContentEvent: CustomerContentEvent): IActivityScope;
}

/**
 * A TelemetryLogger that includes more advanced functions than the SimpleTelemetryLogger,
 * specifically activities and errors.
 */
export class TelemetryLogger extends SimpleTelemetryLogger implements ITelemetryLogger {
  private baseCv: CV;
  constructor(parent?: ISimpleTelemetryLogger, persistentDataFields?: DataField[], config?: ITelemetryConfig) {
    super(parent, persistentDataFields, config);
    this.baseCv = getBaseCv();
  }

  /**
   * Creates a new activity. The caller is responsible for setting the result and calling endNow.
   * @param telemetryEvent - The underlying telemetry event for this activity
   */
  public createActivity(telemetryEvent: TelemetryEvent): IActivityScope {
    return createActivity({
      sendTelemetryEvent: this.sendTelemetryEvent.bind(this),
      telemetryEvent,
      parentCv: this.baseCv
    });
  }

  /**
   * Advanced usage: Sends an activity. The caller is responsible for setting all of the activity parameters.
   * @param activityName - The name of the activity
   * @param activity - The activity contract
   * @param dataFields - Optional additional data fields
   * @param eventFlags - Event flags
   */
  public sendActivity(activityName: string, activity: Activity.Contract, dataFields: DataField[], eventFlags: EventFlags) {
    return this.sendTelemetryEvent({
      eventName: activityName,
      eventContract: {
        name: Activity.contractName,
        dataFields: Activity.getFields(activity)
      },
      dataFields,
      eventFlags
    });
  }

  /**
   * Sends an event with the Error data contract. A convenience wrapper around sendTelemetryEvent, with
   * an Error as a data field.
   * @param error - an ErrorEvent that represents the error
   */
  public sendError(error: ErrorEvent) {
    let dataFields: DataField[] = Error.getFields(error.error);
    if (error.dataFields != null) {
      dataFields.push(...error.dataFields);
    }
    return this.sendTelemetryEvent({
      eventName: error.eventName,
      dataFields,
      eventFlags: error.eventFlags
    });
  }

  /**
   * Creates a customer content activity, which should be ended by the caller by calling endNow().
   * @param telemetryEvent - the underlying telemetry event for the activity
   * @param customerContentType - the type of the customer content
   */
  public createCustomerContentActivity(customerContentEvent: CustomerContentEvent): IActivityScope {
    return createActivity({
      sendTelemetryEvent: (this.sendCustomerContent as (t: TelemetryEvent) => void).bind(this),
      telemetryEvent: customerContentEvent,
      parentCv: this.baseCv
    });
  }
}
